import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Minus, Plus, ShoppingCart, ArrowLeft, PackageCheck, Truck, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { PRODUCTS, CATEGORIES } from '../data/mock';
import ProductCard from '../components/ProductCard';
import { useToast } from '../hooks/use-toast';
import { getPrimaryImage } from '../lib/imageUtils';
import { fileUrl } from '../lib/api';
import { useCatalog } from '../lib/catalog';
import { MIN_QTY } from '../lib/config';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { products: activeProducts } = useCatalog();
  const pool = activeProducts && activeProducts.length ? activeProducts : PRODUCTS;
  const product = pool.find((p) => p.id === id);
  const { addItem, getUnitPrice } = useCart();
  const { toast } = useToast();
  const [qty, setQty] = useState(MIN_QTY);
  const [selectedImg, setSelectedImg] = useState(0);

  // Reset local state whenever the product id changes (navigation between products)
  useEffect(() => {
    setQty(MIN_QTY);
    setSelectedImg(0);
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [id]);

  if (!product) {
    return (
      <div className="max-w-4xl mx-auto p-8 text-center">
        <p className="text-gray-600">Product not found.</p>
        <Link to="/shop" className="text-emerald-700 hover:underline">Back to Shop</Link>
      </div>
    );
  }

  const cat = CATEGORIES.find((c) => c.id === product.category);
  const related = pool.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 6);
  const unitPrice = getUnitPrice(product, qty);
  const discount = Math.round(((product.mrp - product.price) / product.mrp) * 100);
  const gallery = product.images && product.images.length > 0 ? product.images : [product.image].filter(Boolean);

  const handleAdd = () => {
    addItem(product, qty);
    toast({ title: 'Added to cart', description: `${qty} × ${product.name}` });
  };

  const handleBuyNow = () => {
    addItem(product, qty);
    navigate('/cart');
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="text-xs text-gray-500 mb-4">
          <Link to="/" className="hover:text-emerald-700">Home</Link> /
          <Link to="/shop" className="hover:text-emerald-700 ml-1">Shop</Link> /
          {cat && <Link to={`/shop?category=${cat.id}`} className="hover:text-emerald-700 ml-1">{cat.name}</Link>} /
          <span className="ml-1 text-gray-700">{product.name}</span>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-3 md:p-6 grid md:grid-cols-2 gap-4 md:gap-8">
          <div>
            <div className="aspect-square bg-gray-50 rounded-lg overflow-hidden">
              <img src={fileUrl(gallery[selectedImg] || getPrimaryImage(product))} alt={product.name} className="w-full h-full object-cover" />
            </div>
            {gallery.length > 1 && (
              <div className="mt-3 grid grid-cols-4 gap-2">
                {gallery.map((src, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImg(idx)}
                    className={`aspect-square rounded overflow-hidden border-2 ${selectedImg === idx ? 'border-emerald-600' : 'border-gray-200 hover:border-gray-400'}`}
                  >
                    <img src={fileUrl(src)} alt={`view-${idx}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-col">
            <button
              onClick={() => navigate(-1)}
              className="self-start text-xs text-gray-500 hover:text-emerald-700 flex items-center gap-1 mb-2"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
            <h1 className="text-xl md:text-3xl font-bold text-gray-900 leading-snug">{product.name}</h1>
            <div className="text-xs text-gray-500 mt-1">SKU: {product.sku} | Category: {cat?.name}</div>
            <div className="mt-1 inline-block px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 text-[11px] font-semibold">Minimum Order: {MIN_QTY} pcs</div>

            <div className="mt-3 md:mt-4 flex items-baseline gap-2 md:gap-3 flex-wrap">
              <span className="text-2xl md:text-3xl font-bold text-emerald-700">₹{unitPrice.toFixed(2)}</span>
              <span className="text-base md:text-lg text-gray-400 line-through">₹{product.mrp.toFixed(2)}</span>
              {discount > 0 && (
                <span className="bg-orange-100 text-orange-700 text-xs font-bold px-2 py-1 rounded">
                  {discount}% OFF
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500">Price exclusive of 18% GST</div>

            {product.tier1 && (
              <div className="mt-4 grid grid-cols-3 gap-2 text-sm">
                <div className="border rounded p-2 text-center">
                  <div className="text-[10px] text-gray-500">1+ qty</div>
                  <div className="font-semibold text-gray-800">₹{product.price.toFixed(2)}</div>
                </div>
                <div className="border rounded p-2 text-center bg-emerald-50 border-emerald-200">
                  <div className="text-[10px] text-gray-500">{product.tier1.qty}+ qty</div>
                  <div className="font-semibold text-emerald-700">₹{product.tier1.price.toFixed(2)}</div>
                </div>
                <div className="border rounded p-2 text-center bg-emerald-50 border-emerald-200">
                  <div className="text-[10px] text-gray-500">{product.tier2.qty}+ qty</div>
                  <div className="font-semibold text-emerald-700">₹{product.tier2.price.toFixed(2)}</div>
                </div>
              </div>
            )}

            <p className="mt-4 text-sm text-gray-600 leading-relaxed">{product.description}</p>

            <div className="mt-4 text-xs">
              <span className={`inline-block px-2 py-1 rounded font-medium ${product.stock > 20 ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'}`}>
                {product.stock > 0 ? `In Stock: ${product.stock} units` : 'Out of Stock'}
              </span>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-2 md:gap-3">
              <div className="flex items-center border border-gray-300 rounded">
                <button onClick={() => setQty(Math.max(MIN_QTY, qty - 1))} className="px-3 py-2 hover:bg-gray-100">
                  <Minus className="w-4 h-4" />
                </button>
                <input
                  type="number"
                  min={MIN_QTY}
                  value={qty}
                  onChange={(e) => setQty(Math.max(MIN_QTY, parseInt(e.target.value) || MIN_QTY))}
                  className="w-14 text-center py-2 focus:outline-none"
                />
                <button onClick={() => setQty(qty + 1)} className="px-3 py-2 hover:bg-gray-100">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <Button onClick={handleAdd} variant="outline" className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 flex-1 sm:flex-initial">
                <ShoppingCart className="w-4 h-4 mr-2" /> Add to Cart
              </Button>
              <Button onClick={handleBuyNow} className="bg-orange-500 hover:bg-orange-600 text-white flex-1 sm:flex-initial">
                Buy Now
              </Button>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-2 text-xs text-gray-600 border-t pt-4">
              <div className="flex items-center gap-1"><Truck className="w-4 h-4 text-emerald-600" /> Fast Delivery</div>
              <div className="flex items-center gap-1"><PackageCheck className="w-4 h-4 text-emerald-600" /> Bulk Discount</div>
              <div className="flex items-center gap-1"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Quality Assured</div>
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <section className="mt-10">
            <h2 className="text-lg font-bold text-gray-800 mb-4">Related Products</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {related.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
